<template>
  <div class="data-picker-container">
    <input
      ref="datePicker"
      :disabled="disabled"
      class="flatpickr absolute-view active"
      type="text"
      readonly="readonly"
    >
    <gx-select
      :searchable="false"
      :value="dateRange"
      :options="dateRangeOptions"
      :disabled="disabled"
      track-by="value"
      label="label"
      @input="updateDateRange"
    >
      <template #selectedLabel="{ option }">
        {{ option.value === 'custom' ? getCustomDateRange : getFormattedDateRange(option) }}
      </template>
      <template #option="{ option }">
        {{ option.label }}
        <div
          v-if="option.value === 'custom'"
          class="absolute-view"
          @click="openDatePicker"
        />
      </template>
    </gx-select>
  </div>
</template>

<script>
import FlatPickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.css'

import subMonths from 'date-fns/subMonths'
import startOfYesterday from 'date-fns/startOfYesterday'
import endOfYesterday from 'date-fns/endOfYesterday'
import startOfToday from 'date-fns/startOfToday'
import endOfToday from 'date-fns/endOfToday'
import startOfDay from 'date-fns/startOfDay'
import endOfDay from 'date-fns/endOfDay'
import subDays from 'date-fns/subDays'
import startOfWeek from 'date-fns/startOfWeek'
import subWeeks from 'date-fns/subWeeks'
import endOfWeek from 'date-fns/endOfWeek'
import lastDayOfMonth from 'date-fns/lastDayOfMonth'
import startOfMonth from 'date-fns/startOfMonth'
import format from 'date-fns/format'

export default {
  name: 'MultiSelectDateRangePicker',
  components: {
    FlatPickr,
  },
  props: {
    dateRange: {
      type: Object,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showDateRange: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dateRangeOptions: [
        { value: 'yesterday', label: this.$t('date.yesterday') },
        { value: 'lastSevenDays', label: this.$t('date.last7Days') },
        { value: 'thisWeek', label: this.$t('date.thisWeek') },
        { value: 'lastWeek', label: this.$t('date.lastWeek') },
        { value: 'thisMonth', label: this.$t('date.thisMonth') },
        { value: 'lastMonth', label: this.$t('date.lastMonth') },
        { value: 'lastThirtyDays', label: this.$t('date.last30Days') },
        { value: 'custom', label: this.$t('date.custom') },
      ],
      selectedCustomDateRange: [],
    }
  },
  computed: {
    getCustomDateRange() {
      const dateRange = this.selectedCustomDateRange
      if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) return this.$t('error.errorDate')

      const startDate = dateRange[0]
      const endDate = dateRange[1]
      return `${format(startDate, 'yyyy-MM-dd')} to ${format(endDate, 'yyyy-MM-dd')}`
    },
  },
  watch: {
    dateRange(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$emit('selectedDateRange', this.getDatesForSelectedDateRange(newValue))
      }
    },
  },
  mounted() {
    // Make sure parent recieves the selected date range of the passed in initial dateRange.
    this.$emit('selectedDateRange', this.getDatesForSelectedDateRange(this.dateRange))

    this.flatpickrObject = FlatPickr(this.$refs.datePicker, {
      mode: 'range',
      maxDate: 'today',
      onChange: (selectedDates) => {
        this.selectedCustomDateRange = selectedDates

        // Only count as a successful select after start and end dates are picked.
        if (selectedDates.length > 1) {
          this.$emit('update:dateRange', { value: 'custom', label: this.$t('date.custom') })
        }
      },
    })
  },
  methods: {
    openDatePicker() {
      this.flatpickrObject.open()
    },
    updateDateRange(dateRange) {
      if (dateRange && dateRange.value !== 'custom') {
        this.$emit('update:dateRange', dateRange)
        this.flatpickrObject.close()
      } else {
        this.openDatePicker()
      }
    },
    getDatesForSelectedDateRange(selectedOption) {
      if (!selectedOption) {
        return []
      }
      switch (selectedOption.value) {
        case 'yesterday':
          return [startOfYesterday(), endOfYesterday()]
        case 'lastSevenDays':
          return [subDays(startOfToday(), 7), endOfToday()]
        case 'thisWeek':
          return [startOfWeek(new Date()), endOfWeek(new Date())]
        case 'lastWeek': {
          const lastWeekToday = subWeeks(new Date(), 1)
          return [startOfWeek(lastWeekToday), endOfWeek(lastWeekToday)]
        }
        case 'thisMonth': {
          return [startOfMonth(new Date()), endOfDay(lastDayOfMonth(new Date()))]
        }
        case 'lastMonth': {
          const lastMonthToday = subMonths(new Date(), 1)
          return [startOfMonth(lastMonthToday), endOfDay(lastDayOfMonth(lastMonthToday))]
        }
        case 'lastThirtyDays':
          return [subDays(startOfToday(), 30), endOfToday()]
        case 'custom': {
          const startDate = this.selectedCustomDateRange[0]
          const endDate = this.selectedCustomDateRange[1]
          if (startDate.getTime() === endDate.getTime()) {
            return [startOfDay(endDate), endOfDay(startDate)]
          }
          return [startOfDay(startDate), endOfDay(endDate)]
        }
        default:
          return [new Date(), new Date()]
      }
    },
    getFormattedDateRange(option) {
      if (this.showDateRange) {
        const range = this.getDatesForSelectedDateRange(option)
        const dateFormat = 'd MMM yyyy'
        return range.map(date => format(date, dateFormat)).join(' - ')
      }
      return option.label
    },
  },
}
</script>

<style lang="scss" scoped>
.data-picker-container {
  position: relative;
  // Make the hack button span the whole parent container.
  .absolute-view {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
  }
}
</style>

